// ============================================
// CFO View
// Accounting-grade dashboard with P&L, cash
// position, inter-entity ledger, reconciliation
// status, and export controls
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Link2,
  CheckCircle2,
  Circle,
  FileSpreadsheet,
  Download,
  Building2,
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { C_DOC_BRANDS } from '../../../constants/collections';
import { InterEntityLedger } from './InterEntityLedger';
import type { Order } from '../../../types/order';
import type { BrandConfig } from '../../../types/docops';

// --- Props ---

interface CfoViewProps {
  orders: Order[];
  totalRevenueCents: number;
}

// --- Helpers ---

const REVENUE_STATUSES = ['paid', 'processing', 'shipped', 'delivered'] as const;

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function isCurrentMonth(dateStr: string): boolean {
  const now = new Date();
  const d = new Date(dateStr);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

// --- Reconciliation types ---

interface ReconCard {
  label: string;
  status: 'reconciled' | 'not_connected';
  detail: string;
}

// --- Component ---

export function CfoView({ orders }: CfoViewProps) {
  const [brandConfig, setBrandConfig] = useState<BrandConfig | null>(null);
  const [brandLoading, setBrandLoading] = useState(true);

  // Fetch brand config from Firestore
  useEffect(() => {
    async function fetchBrand() {
      try {
        const snap = await getDocs(collection(db, C_DOC_BRANDS));
        if (!snap.empty) {
          const firstBrand = snap.docs[0].data();
          if (firstBrand.config) {
            setBrandConfig(firstBrand.config as BrandConfig);
          }
        }
      } catch {
        // Brand config not available — show placeholder in ledger
      } finally {
        setBrandLoading(false);
      }
    }
    fetchBrand();
  }, []);

  // P&L calculations (MTD)
  const pnl = useMemo(() => {
    const mtdPaidOrders = orders.filter(
      (o) =>
        REVENUE_STATUSES.includes(o.status as (typeof REVENUE_STATUSES)[number]) &&
        o.paidAt &&
        isCurrentMonth(o.paidAt),
    );

    const revenueCents = mtdPaidOrders.reduce((sum, o) => sum + o.totalInCents, 0);
    const cogsCents = Math.round(revenueCents * 0.38);
    const grossProfitCents = revenueCents - cogsCents;
    const opexCents = Math.round(revenueCents * 0.27);
    const netIncomeCents = grossProfitCents - opexCents;

    return { revenueCents, cogsCents, grossProfitCents, opexCents, netIncomeCents };
  }, [orders]);

  // Monthly revenue for inter-entity ledger
  const monthlyRevenueCents = pnl.revenueCents;

  // Reconciliation status
  const reconCards: ReconCard[] = useMemo(() => {
    const paidOrders = orders.filter((o) =>
      REVENUE_STATUSES.includes(o.status as (typeof REVENUE_STATUSES)[number]),
    );
    const stripeLabel =
      paidOrders.length === orders.filter((o) => o.status !== 'pending' && o.status !== 'cancelled').length
        ? 'Reconciled'
        : `${paidOrders.length} of ${orders.length} orders`;

    return [
      {
        label: 'Stripe <> Orders',
        status: 'reconciled' as const,
        detail: stripeLabel,
      },
      {
        label: 'QuickBooks Online',
        status: 'not_connected' as const,
        detail: 'Not Connected',
      },
      {
        label: 'Pharmacy',
        status: 'not_connected' as const,
        detail: 'Not Connected',
      },
    ];
  }, [orders]);

  // P&L row definitions
  const pnlRows = [
    { label: 'Revenue', cents: pnl.revenueCents, bold: false, colorClass: 'text-text-primary' },
    { label: 'COGS (est. 38%)', cents: -pnl.cogsCents, bold: false, colorClass: 'text-suboptimal' },
    { label: 'Gross Profit', cents: pnl.grossProfitCents, bold: true, colorClass: 'text-optimal' },
    { label: 'OpEx (est. 27%)', cents: -pnl.opexCents, bold: false, colorClass: 'text-suboptimal' },
    { label: 'Net Income', cents: pnl.netIncomeCents, bold: true, colorClass: pnl.netIncomeCents >= 0 ? 'text-optimal' : 'text-critical' },
  ];

  // Export buttons
  const exportButtons = [
    { label: 'P&L CSV', icon: Download },
    { label: 'Balance Sheet CSV', icon: Download },
    { label: 'Tax Report CSV', icon: FileSpreadsheet },
  ];

  return (
    <div className="space-y-6">
      {/* P&L Summary (MTD) */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6"
      >
        <h3 className="text-lg font-display text-text-primary mb-4">
          P&L Summary (MTD)
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <tbody>
              {pnlRows.map((row, i) => (
                <tr
                  key={row.label}
                  className={
                    i < pnlRows.length - 1
                      ? 'border-b border-bg-tertiary'
                      : 'border-t-2 border-bg-tertiary'
                  }
                >
                  <td
                    className={`py-3 text-text-secondary ${
                      row.bold ? 'font-semibold' : ''
                    }`}
                  >
                    {row.label}
                  </td>
                  <td
                    className={`py-3 text-right tabular-nums font-medium whitespace-nowrap ${row.colorClass} ${
                      row.bold ? 'font-bold text-lg' : ''
                    }`}
                  >
                    {row.cents < 0 ? '(' : ''}
                    {formatCurrency(Math.abs(row.cents))}
                    {row.cents < 0 ? ')' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm text-text-muted mt-4 italic">
          Estimates based on industry benchmarks. Connect QBO for actual data.
        </p>
      </motion.div>

      {/* Cash Position (QBO placeholder) */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.35 }}
        className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6"
      >
        <h3 className="text-lg font-display text-text-primary mb-4">
          Cash Position
        </h3>

        <div className="flex flex-col items-center text-center py-6">
          <div className="w-14 h-14 rounded-xl bg-gold/10 flex items-center justify-center mb-4">
            <Link2 size={24} className="text-gold" />
          </div>
          <p className="text-base text-text-secondary mb-2 max-w-md">
            QuickBooks Online integration is ready. Connect your QBO company file
            to see live cash position, AR, AP, and runway.
          </p>
          <div className="relative mt-4">
            <button
              disabled
              className="px-6 py-3 bg-bg-elevated rounded-xl text-text-muted cursor-not-allowed opacity-60 text-base font-medium flex items-center gap-2"
            >
              <Link2 size={16} />
              Connect QuickBooks
            </button>
            <span className="absolute -top-2 -right-2 bg-gold/20 text-gold text-xs font-medium px-2 py-0.5 rounded-full border border-gold/30">
              Coming Soon
            </span>
          </div>
        </div>
      </motion.div>

      {/* Inter-Entity Ledger */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.35 }}
      >
        {brandLoading ? (
          <div className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 size={20} className="text-gold" />
              <h3 className="text-lg font-display text-text-primary">
                Inter-Entity Ledger
              </h3>
            </div>
            <p className="text-text-muted text-base">Loading brand configuration...</p>
          </div>
        ) : (
          <InterEntityLedger
            monthlyRevenueCents={monthlyRevenueCents}
            brandConfig={brandConfig}
          />
        )}
      </motion.div>

      {/* Reconciliation Status */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.35 }}
        className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6"
      >
        <h3 className="text-lg font-display text-text-primary mb-4">
          Reconciliation Status
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {reconCards.map((card) => (
            <div
              key={card.label}
              className="bg-bg-elevated border border-bg-tertiary rounded-lg p-4"
            >
              <p className="text-sm text-text-muted uppercase tracking-wider mb-2">
                {card.label}
              </p>
              <div className="flex items-center gap-2">
                {card.status === 'reconciled' ? (
                  <CheckCircle2 size={18} className="text-optimal flex-shrink-0" />
                ) : (
                  <Circle size={18} className="text-text-muted flex-shrink-0" />
                )}
                <span
                  className={`text-base font-medium ${
                    card.status === 'reconciled' ? 'text-optimal' : 'text-text-muted'
                  }`}
                >
                  {card.detail}
                </span>
              </div>
              {card.status === 'not_connected' && (
                <div className="flex items-center gap-1 mt-2">
                  <Link2 size={12} className="text-text-muted" />
                  <span className="text-xs text-text-muted">Connect to enable</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Export Buttons Row */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.35 }}
        className="flex flex-wrap gap-3"
      >
        {exportButtons.map((btn) => (
          <div key={btn.label} className="relative group">
            <button
              disabled
              className="px-4 py-2 bg-bg-elevated rounded-lg text-text-muted cursor-not-allowed opacity-60 text-base flex items-center gap-2"
            >
              <btn.icon size={16} />
              {btn.label}
            </button>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-bg-deep border border-bg-tertiary rounded-lg text-sm text-text-muted whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Connect QBO to enable
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
